import React from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  LabelProps,
  ResponsiveContainer,
  XAxis,
} from 'recharts';
import { Theme, makeStyles } from '@material-ui/core/styles';

export type DashboardBarChartItem = { label: string; value: number };
const useStyles = makeStyles<Theme, { clickable: boolean }>((theme) => ({
  bar: {
    fill: `${theme.palette.primary.main}77`,
    '&:hover': {
      fill: `${theme.palette.primary.main}`,
      cursor: (props) => (props.clickable ? 'pointer' : undefined),
    },
  },
  barHovered: {
    fill: theme.palette.primary.main,
  },
  label: {
    fill: 'black',
  },
  clickableLabel: {
    cursor: 'pointer',
  },
  background: {
    cursor: (props) => (props.clickable ? 'pointer' : undefined),
  },
}));

type BarBackgroundProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
};

export function DashboardBarChart({
  data,
  height = 400,
  onBarClick,
}: {
  data: DashboardBarChartItem[];
  height?: number;
  onBarClick?: (data: DashboardBarChartItem, index?: number) => void;
}) {
  const classes = useStyles({ clickable: Boolean(onBarClick) });
  const [hoveredIndex, setHoveredIndex] = React.useState<number>();
  const handleBarClick = (
    data: { payload?: DashboardBarChartItem },
    index?: number
  ) => {
    if (data.payload) {
      onBarClick?.(data.payload, index);
    }
  };

  const handleItemClick = (index?: number) => {
    if (index === undefined) {
      return;
    }

    const item = data[index];

    if (item) {
      onBarClick?.(item, index);
    }
  };

  const renderLabel = (props: LabelProps) => {
    const { index, value } = props as LabelProps & { index?: number };
    const x = Number(props.x);
    const y = Number(props.y);
    const width = Number(props.width);

    if (
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(width) ||
      value === undefined ||
      value === null
    ) {
      return <g />;
    }

    const clickable =
      Boolean(onBarClick) && index !== undefined && Boolean(data[index]);
    const className = [
      props.className,
      classes.label,
      clickable ? classes.clickableLabel : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#000"
        textAnchor="middle"
        className={className}
        onClick={clickable ? () => handleItemClick(index) : undefined}
        onMouseEnter={clickable ? () => setHoveredIndex(index) : undefined}
        onMouseLeave={clickable ? () => setHoveredIndex(undefined) : undefined}
      >
        {value}
      </text>
    );
  };

  const renderBackground = (props: unknown) => {
    const { x, y, width, height, index } = props as BarBackgroundProps;

    if (
      x === undefined ||
      y === undefined ||
      width === undefined ||
      height === undefined ||
      index === undefined ||
      !data[index]
    ) {
      return <g />;
    }

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="transparent"
        className={classes.background}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => setHoveredIndex(undefined)}
        onClick={() => handleItemClick(index)}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        width={150}
        height={40}
        margin={{ top: 50, left: 0, right: 0, bottom: 0 }}
        data={data}
      >
        <Bar
          dataKey="value"
          className={classes.bar}
          onClick={onBarClick ? handleBarClick : undefined}
          background={onBarClick ? renderBackground : undefined}
          isAnimationActive={false}
        >
          {data.map((_, index) => (
            <Cell
              key={index}
              className={[
                classes.bar,
                hoveredIndex === index ? classes.barHovered : undefined,
              ]
                .filter(Boolean)
                .join(' ')}
            />
          ))}
          <LabelList position="top" content={renderLabel} />
        </Bar>
        <XAxis dataKey="label" allowDataOverflow={true} />
      </BarChart>
    </ResponsiveContainer>
  );
}
